// Wizard pending-primitive fallbacks for the 2 logical types declared with
// pending_primitive: true across CHG-1241 manifests: text + checkbox.
//
// Mirrors SettingsPendingField (CHG-1239) but as separate top-level
// components per type so each can register into FormDialog's fieldRegistry
// at module load via ./registerWizardPrimitives.ts. When the field-primitive
// registry expansion CHG ships these types natively, both files can be
// deleted and FormDialog/Settings consume the native primitives directly.
//
// Both wrappers also handle the `props.rendered_by_component: <id>` opt-in
// for custom-render dispatch via customRenderRegistry (used by the import-map
// wizard's column_mapping field).

import type { ReactNode } from "react";
import { getWizardComponent, unregisteredStub, type CustomRenderProps } from "./customRenderRegistry";

interface BaseProps {
  name: string;
  value: unknown;
  onChange: (value: unknown) => void;
  label?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  /** Forwarded from manifest field.props (spread by FormDialog). */
  [key: string]: unknown;
}

const BASE_INPUT_STYLE: React.CSSProperties = {
  height: 36,
  padding: "0 12px",
  borderRadius: "var(--radius-md)",
  border: "1px solid var(--color-border)",
  background: "var(--color-surface)",
  fontSize: 13,
  color: "var(--color-text)",
  width: "100%",
};

const LABEL_STYLE: React.CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-muted)",
  marginBottom: 4,
};

/** Look up custom-render id from props; render the registered component or
 *  the unregistered stub. */
function tryCustomRender(props: BaseProps): ReactNode | null {
  const id = props.rendered_by_component;
  if (typeof id !== "string" || !id) return null;
  const Comp = getWizardComponent(id);
  if (!Comp) return unregisteredStub(id);
  return <Comp {...(props as CustomRenderProps)} />;
}

function labelLine(label: string | undefined, required: boolean | undefined): ReactNode {
  if (!label) return null;
  return (
    <span style={LABEL_STYLE}>
      {label}
      {required ? <span style={{ color: "var(--color-error)" }}> *</span> : null}
    </span>
  );
}

/** WizardPendingTextField — handles text + enum (select-like) + masked + custom-render dispatch. */
export function WizardPendingTextField(props: BaseProps) {
  const custom = tryCustomRender(props);
  if (custom) return <>{custom}</>;
  const { name, value, onChange, label, helperText, required, disabled } = props;
  const enumOptions = Array.isArray(props.enum) ? (props.enum as string[]) : null;
  const masked = props.masked === true;
  const stringValue = value == null ? "" : typeof value === "string" ? value : String(value);

  if (enumOptions) {
    return (
      <label data-wizard-pending="enum" style={{ display: "flex", flexDirection: "column" }}>
        {labelLine(label, required)}
        <select
          name={name}
          value={stringValue}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
          style={BASE_INPUT_STYLE}
        >
          <option value="">— select —</option>
          {enumOptions.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        {helperText ? <small style={{ color: "var(--color-text-muted)" }}>{helperText}</small> : null}
      </label>
    );
  }

  return (
    <label data-wizard-pending="text" style={{ display: "flex", flexDirection: "column" }}>
      {labelLine(label, required)}
      <input
        type={masked ? "password" : "text"}
        name={name}
        value={stringValue}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
        style={BASE_INPUT_STYLE}
      />
      {helperText ? <small style={{ color: "var(--color-text-muted)" }}>{helperText}</small> : null}
    </label>
  );
}

/** WizardPendingCheckboxField — handles boolean toggles. */
export function WizardPendingCheckboxField(props: BaseProps) {
  const custom = tryCustomRender(props);
  if (custom) return <>{custom}</>;
  const { name, value, onChange, label, helperText, disabled } = props;
  const boolValue = !!value;
  return (
    <label
      data-wizard-pending="checkbox"
      style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--color-text)" }}
    >
      <input
        type="checkbox"
        name={name}
        checked={boolValue}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
      {helperText ? (
        <small style={{ color: "var(--color-text-muted)", marginLeft: 8 }}>{helperText}</small>
      ) : null}
    </label>
  );
}
